/**
 * SignUpPage.js — Shown when Clerk restricted mode is ON.
 * No Clerk SignUp component — just a clean "request access" panel.
 * If restricted mode is later turned off, swap the right panel for <SignUp>.
 */

import React from 'react';

const SignUpPage = () => (
  <div style={s.root}>
    <style>{css}</style>

    {/* Left panel — identical to SignInPage */}
    <div style={s.left}>
      <div style={s.leftInner}>
        <a href="https://testmyagent.io" style={s.logoLink}>
          <div style={s.logoMark}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="1" y="3" width="10" height="2" rx="1" fill="white"/>
              <rect x="1" y="7.5" width="7.5" height="2" rx="1" fill="rgba(255,255,255,0.55)"/>
              <rect x="1" y="12" width="5" height="2" rx="1" fill="rgba(255,255,255,0.3)"/>
              <circle cx="14.5" cy="4" r="3" fill="#00E5A0"/>
              <path d="M13 4L14.1 5.1L16.2 2.9" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={s.logoText}>test_my_<em style={s.logoEm}>agent</em></span>
        </a>

        <div>
          <h1 style={s.h1}>Test your Zendesk AI Agent<br/><em style={s.h1Accent}>before your customers do.</em></h1>
          <p style={s.subtext}>Import real ticket history. Simulate conversations. Get a scored benchmark report in under 30 minutes.</p>
        </div>

        <div style={s.statsGrid}>
          {[
            ['67%', 'AI win rate in typical benchmark'],
            ['$2,847', 'Avg. projected monthly savings'],
            ['<30 min', 'From connection to benchmark'],
          ].map(([num, label]) => (
            <div key={num} style={s.statCard}>
              <div style={s.statNum}>{num}</div>
              <div style={s.statLabel}>{label}</div>
            </div>
          ))}
        </div>

        <ul style={s.featureList}>
          {['Human vs AI Benchmark with ROI projection','KB groundedness scoring — catch hallucinations','Prompt injection and security testing','A/B configuration comparison (Pro)'].map((f, i) => (
            <li key={i} style={s.featureItem}><span style={s.dot}/>{f}</li>
          ))}
        </ul>

        <div style={s.badge}><span style={s.badgeDot}/>PRIVATE BETA · LIMITED SPOTS AVAILABLE</div>
      </div>
    </div>

    {/* Right panel — restricted access message */}
    <div style={s.right}>
      <div style={s.rightInner}>

        {/* Lock icon */}
        <div style={s.lockWrap}>
          <div style={s.lockIcon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9b7eff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
        </div>

        <div>
          <h2 style={s.formTitle}>Private beta</h2>
          <p style={s.formDesc}>
            Test My Agent is currently invite-only. We review every request
            and respond within 24 hours.
          </p>
        </div>

        {/* Email CTA */}
        <a
          href="mailto:fernando@cxperiences.com?subject=Beta%20Access%20Request"
          style={s.emailBtn}
        >
          Request beta access →
        </a>

        <div style={s.divider}/>

        {/* What to include */}
        <div style={s.hint}>
          <div style={s.hintLabel}>Include in your email:</div>
          <ul style={s.hintList}>
            <li style={{fontSize:'13px',color:'#9e9bbf',fontWeight:'300',paddingLeft:'12px',borderLeft:'1px solid rgba(107,70,250,0.3)',listStyle:'none'}}>Your Zendesk subdomain (e.g. acme.zendesk.com)</li>
            <li style={{fontSize:'13px',color:'#9e9bbf',fontWeight:'300',paddingLeft:'12px',borderLeft:'1px solid rgba(107,70,250,0.3)',listStyle:'none'}}>Your role (VP Support, Director of CX, etc.)</li>
            <li style={{fontSize:'13px',color:'#9e9bbf',fontWeight:'300',paddingLeft:'12px',borderLeft:'1px solid rgba(107,70,250,0.3)',listStyle:'none'}}>Your expected monthly ticket volume</li>
          </ul>
        </div>

        <p style={s.signinNote}>
          Already have access?{' '}
          <a href="https://testmyagent.io/sign-in" style={s.link}>Sign in here</a>
        </p>

      </div>
    </div>
  </div>
);

const css = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&family=IBM+Plex+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
  @media (max-width: 860px) { .auth-left { display: none !important; } }
`;

const s = {
  root: { display: 'flex', minHeight: '100vh', background: '#0a0a12', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" },
  left: { flex: '1 1 50%', background: 'linear-gradient(160deg, rgba(107,70,250,0.1) 0%, transparent 60%), #0a0a12', borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', overflow: 'hidden' },
  leftInner: { maxWidth: '420px', width: '100%', display: 'flex', flexDirection: 'column', gap: '1.8rem', animation: 'fadeIn 0.5s ease both' },
  logoLink: { display: 'inline-flex', alignItems: 'center', gap: '10px', textDecoration: 'none' },
  logoMark: { width: '32px', height: '32px', background: '#6b46fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 16px rgba(107,70,250,0.4)' },
  logoText: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '14px', fontWeight: '500', color: '#eeebff', letterSpacing: '0.02em' },
  logoEm: { fontStyle: 'normal', color: '#9b7eff' },
  h1: { fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: '600', lineHeight: '1.22', letterSpacing: '-0.02em', color: '#eeebff', margin: '0 0 0.8rem' },
  h1Accent: { color: '#9b7eff', fontStyle: 'italic', fontWeight: '300' },
  subtext: { fontSize: '14px', color: '#9e9bbf', lineHeight: '1.7', fontWeight: '300', margin: 0, paddingLeft: '14px', borderLeft: '2px solid rgba(107,70,250,0.3)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' },
  statCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '14px 12px' },
  statNum: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '18px', fontWeight: '500', color: '#00e5a0', letterSpacing: '-0.02em', marginBottom: '4px' },
  statLabel: { fontSize: '11px', color: '#4e4b6a', lineHeight: '1.4', fontWeight: '300' },
  featureList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' },
  featureItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: '#9e9bbf', fontWeight: '300', lineHeight: '1.5' },
  dot: { width: '5px', height: '5px', background: '#6b46fa', borderRadius: '50%', flexShrink: 0, marginTop: '5px', boxShadow: '0 0 6px rgba(107,70,250,0.5)', display: 'inline-block' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', color: '#9b7eff', letterSpacing: '0.08em', background: 'rgba(107,70,250,0.1)', border: '1px solid rgba(107,70,250,0.25)', padding: '6px 12px', borderRadius: '999px', width: 'fit-content' },
  badgeDot: { width: '5px', height: '5px', background: '#00e5a0', borderRadius: '50%', boxShadow: '0 0 6px rgba(0,229,160,0.7)', animation: 'pulse 2s infinite', display: 'inline-block' },
  // Right panel
  right: { flex: '1 1 50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', background: '#0b0b16' },
  rightInner: { width: '100%', maxWidth: '380px', display: 'flex', flexDirection: 'column', gap: '1.5rem', animation: 'fadeIn 0.5s 0.1s ease both' },
  lockWrap: { display: 'flex', justifyContent: 'center' },
  lockIcon: { width: '64px', height: '64px', background: 'rgba(107,70,250,0.1)', border: '1px solid rgba(107,70,250,0.25)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  formTitle: { fontSize: '22px', fontWeight: '600', color: '#eeebff', letterSpacing: '-0.02em', margin: '0 0 8px' },
  formDesc: { fontSize: '14px', color: '#9e9bbf', fontWeight: '300', lineHeight: '1.7', margin: 0 },
  emailBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6b46fa, #4f2fd4)', color: 'white', padding: '14px 24px', borderRadius: '8px', textDecoration: 'none', fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', fontWeight: '500', letterSpacing: '0.02em', boxShadow: '0 8px 24px rgba(107,70,250,0.3)', transition: 'opacity 0.15s' },
  divider: { height: '1px', background: 'rgba(255,255,255,0.07)' },
  hint: { display: 'flex', flexDirection: 'column', gap: '8px' },
  hintLabel: { fontSize: '11px', color: '#4e4b6a', fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  hintList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' },
  hintListItem: { fontSize: '13px', color: '#9e9bbf', fontWeight: '300', paddingLeft: '12px', borderLeft: '1px solid rgba(107,70,250,0.3)' },
  signinNote: { fontSize: '13px', color: '#4e4b6a', margin: 0, textAlign: 'center' },
  link: { color: '#9b7eff', textDecoration: 'none' },
};

export default SignUpPage;
